import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";

const POINTS = [
  "Single-window coordination",
  "Flexible service options",
  "Clear project scope",
  "Budget-aware planning",
  "Specialist vendor network",
  "Post-project reporting",
];

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function AboutConnect() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="about" ref={ref} className="relative py-20 sm:py-28 scroll-mt-20 overflow-hidden bg-royal-soft">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-gold-dark"
          >
            About OEC
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight text-royal-deep leading-[1.1]"
          >
            Good work happens when{" "}
            <span className="text-gold-dark">everything connects.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-6 text-sm sm:text-lg text-black/75 leading-relaxed"
          >
            We bring planning, creative thinking and reliable execution together — so your team
            has one clear point of contact.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4"
          >
            {POINTS.map((p) => (
              <div key={p} data-testid="about-point" className="flex items-center gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gold-soft border border-gold/50 text-gold-dark">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-sm text-black/80">{p}</span>
              </div>
            ))}
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            href="#enquiry"
            data-testid="about-cta"
            className="group mt-10 inline-flex items-center gap-2.5 rounded-full border border-gold/60 bg-white px-7 py-3.5 text-sm font-semibold text-royal-deep transition-all duration-300 hover:bg-gold hover:text-white hover:shadow-[0_8px_32px_rgba(224,19,39,0.35)]"
          >
            Start a conversation
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute -inset-3 rounded-[28px] border border-gold/50" />
          <div className="relative overflow-hidden rounded-[24px] border border-white shadow-[0_24px_70px_rgba(0,0,0,0.25)]">
            <motion.img
              style={{ y: imgY, scale: 1.12 }}
              src={ABOUT_IMG}
              alt="Speaker addressing a corporate audience"
              data-testid="about-image"
              className="w-full aspect-[4/5] sm:aspect-[5/5] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-royal-deep/85 via-transparent to-royal-deep/10" />
            <div className="absolute bottom-0 inset-x-0 p-7">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold-bright">
                A partner built around your brief
              </p>
              <p className="mt-2 font-display text-lg sm:text-xl font-semibold text-white">
                From first conversation to final delivery.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
